import { useState, useEffect } from 'react';
import AdminLayout from '../AdminLayout';
import { motion } from 'framer-motion';
import apiManager from '../../../../lib/api';
import { SalesAnalytics } from '../../../../lib/api/services/analytics';

const SalesAnalyticsPage = () => {
  const [salesData, setSalesData] = useState<SalesAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  useEffect(() => {
    const fetchSalesData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Obtener datos reales del backend
        const response = await apiManager.analytics.getSalesAnalytics({
          period: dateRange
        });
        
        if (response.success && response.data) {
          setSalesData(response.data);
        } else {
          setError(response.error?.message || 'Error al cargar los datos de ventas');
        }
      } catch (err) {
        console.error('Error al cargar datos de ventas:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSalesData();
  }, [dateRange]);
  
  // Calcular totales
  const totalRevenue = salesData?.dailySales.reduce((acc, day) => acc + day.revenue, 0) || 0;
  const totalOrders = salesData?.dailySales.reduce((acc, day) => acc + day.orders, 0) || 0;
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  
  return (
    <AdminLayout title="Análisis de Ventas">
      {/* Selector de rango de fechas */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Rendimiento de Ventas</h2>
        <div className="flex bg-white rounded-md shadow-sm">
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 text-sm rounded-l-md ${
              dateRange === 'week' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'month' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setDateRange('quarter')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'quarter' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Trimestre
          </button>
          <button
            onClick={() => setDateRange('year')}
            className={`px-4 py-2 text-sm rounded-r-md ${
              dateRange === 'year' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Año
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          <p>{error}</p>
        </div>
      ) : salesData ? (
        <div className="space-y-6">
          {/* Métricas generales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Ingresos Totales</span>
                <span className="text-3xl font-bold text-gray-900">
                  {totalRevenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
                <span className="text-xs text-gray-500 mt-2">
                  Periodo: {dateRange === 'week' ? 'última semana' : 
                            dateRange === 'month' ? 'último mes' : 
                            dateRange === 'quarter' ? 'último trimestre' : 'último año'}
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Pedidos Totales</span>
                <span className="text-3xl font-bold text-gray-900">
                  {totalOrders}
                </span>
                <span className="text-xs text-gray-500 mt-2">
                  {(totalOrders / (dateRange === 'week' ? 7 : 
                                 dateRange === 'month' ? 30 : 
                                 dateRange === 'quarter' ? 90 : 365)).toFixed(1)} pedidos/día
                </span>
              </div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-500">Valor Medio del Pedido</span>
                <span className="text-3xl font-bold text-gray-900">
                  {averageOrderValue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
                <span className="text-xs text-gray-500 mt-2">
                  Por pedido completado
                </span>
              </div>
            </motion.div>
          </div>
          
          {/* Gráfico placeholder - En una implementación real usarías componentes de gráficos */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tendencia de Ventas</h3>
            <div className="h-72 bg-gray-50 rounded-md flex items-center justify-center">
              <p className="text-gray-500">Aquí iría un gráfico de tendencia de ventas diarias</p>
            </div>
          </div>
          
          {/* Productos más vendidos */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Productos Más Vendidos</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Producto
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unidades Vendidas
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ingresos
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesData.topProducts.map((product, index) => (
                    <motion.tr 
                      key={product.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.sales} uds
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Métodos de pago */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Métodos de Pago Utilizados</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {salesData.paymentMethods.map((method, index) => (
                <motion.div 
                  key={method.method}
                  className="p-4 border border-gray-100 rounded-md"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-700">{method.method}</span>
                    <span className="text-sm text-gray-500">{method.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-indigo-600 h-2.5 rounded-full" 
                      style={{ width: `${method.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500 mt-1 block">{method.count} pedidos</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No hay datos disponibles</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default SalesAnalyticsPage; 